let loaderPromise: Promise<void> | null = null;

/**
 * Ensures window.grecaptcha is available, loading Google's reCAPTCHA v3 script
 * once if necessary. Idempotent: reuses an existing <script id="googleRecaptcha">
 * injected by PHP (page_stack_head.php / footerid.php) or a prior call.
 */
export function ensureRecaptcha(siteKey: string): Promise<void> {
    if (!siteKey) return Promise.resolve();
    if (window.grecaptcha) return Promise.resolve();
    if (loaderPromise) return loaderPromise;

    loaderPromise = new Promise<void>((resolve, reject) => {
        // Reuse the same id that PHP uses — prevents a duplicate script tag
        // on pages that already injected it but where grecaptcha hasn't resolved yet.
        const SCRIPT_ID = 'googleRecaptcha';
        if (!document.getElementById(SCRIPT_ID)) {
            const s = document.createElement('script');
            s.id = SCRIPT_ID;
            s.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
            s.async = true;
            s.defer = true;
            s.onerror = () => {
                loaderPromise = null;
                reject(new Error('reCAPTCHA script failed to load'));
            };
            document.head.appendChild(s);
        }

        const start = Date.now();
        const poll = () => {
            if (window.grecaptcha) {
                resolve();
                return;
            }
            if (Date.now() - start > 10_000) {
                loaderPromise = null;
                reject(new Error('reCAPTCHA load timeout'));
                return;
            }
            window.setTimeout(poll, 100);
        };
        poll();
    });

    return loaderPromise;
}
